import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useAuth } from "@/stores/auth";
import { Music2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/signup")({
  head: () => ({ meta: [{ title: "Create account — Sura" }] }),
  component: Signup,
});

function Signup() {
  const navigate = useNavigate();
  const signup = useAuth((s) => s.signup);
  const [form, setForm] = useState({ full_name: "", email: "", phone_number: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form);
      toast.success("Account created");
      navigate({ to: "/onboarding" });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  const f = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  const togglePassword = () => setShowPassword(!showPassword);

  return (
    <div className="min-h-screen bg-secondary/40 flex flex-col items-center justify-center px-4 py-12">
      <Link to="/" className="flex items-center gap-2 mb-8">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
          <Music2 className="h-5 w-5 text-primary-foreground" />
        </div>
        <span className="text-xl font-semibold">Sura</span>
      </Link>
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-[var(--shadow-card)] p-8">
        <h1 className="text-2xl font-semibold tracking-tight">Create your account</h1>
        <p className="mt-1 text-sm text-muted-foreground">Start streaming in under a minute.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {[
            { k: "full_name", label: "Full Name", type: "text", placeholder: "Jane Doe" },
            { k: "email", label: "Email", type: "email", placeholder: "you@example.com" },
            { k: "phone_number", label: "Phone", type: "tel", placeholder: "+1 555 000 0000" },
          ].map((field) => (
            <div key={field.k}>
              <label className="text-sm font-medium">{field.label}</label>
              <input
                required
                type={field.type}
                value={(form as any)[field.k]}
                onChange={f(field.k as any)}
                placeholder={field.placeholder}
                className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          ))}
          <div className="relative">
            <label className="text-sm font-medium">Password</label>
            <input
              required
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={f("password")}
              className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring pr-10"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={togglePassword}
              className="absolute right-3 top-9 p-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <button
            disabled={loading}
            className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-60"
          >
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>
        <p className="mt-6 text-sm text-center text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="text-primary font-medium hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
